import { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Globe, Instagram, Facebook, Twitter, Youtube, Wallet, Banknote, Loader2, Check, X, AtSign } from "lucide-react";
import { checkUsernameAvailable } from "@/lib/api";
import AiHelperDialog from "@/components/AiHelperDialog";
import HtmlWidget from "@/components/HtmlWidget";
import HomePostsPicker from "@/components/HomePostsPicker";
import DealerPageCard from "@/components/DealerPageCard";

// Shared profile building blocks — used by the Profile tab AND the Setup wizard (same UI, same fields).
export const INPUT_CLS = "h-11 rounded-xl";

export function Grid2({ children }) {
  return <div className="grid grid-cols-2 gap-3">{children}</div>;
}

export function Field({ label, icon: Icon, hint, testId, children }) {
  return (
    <div data-testid={testId}>
      <Label className="text-xs font-medium text-zinc-700 flex items-center gap-1.5">
        {Icon ? <Icon size={12} className="text-zinc-400" /> : null}
        {label}
      </Label>
      <div className="mt-1">{children}</div>
      {hint && <div className="text-[11px] text-zinc-400 mt-1">{hint}</div>}
    </div>
  );
}

export function UsernameField({ value, onChange, recId }) {
  const [check, setCheck] = useState({ status: "idle", available: null, message: "" });
  useEffect(() => {
    const v = (value || "").trim();
    if (v.length < 3) { setCheck({ status: "idle", available: null, message: "" }); return undefined; }
    const t = setTimeout(async () => {
      setCheck((c) => ({ ...c, status: "checking" }));
      try {
        const res = await checkUsernameAvailable(v, recId);
        setCheck({ status: "done", available: res?.available, message: res?.available ? "Available" : res?.message || "Already taken" });
      } catch {
        setCheck({ status: "done", available: null, message: "" });
      }
    }, 350);
    return () => clearTimeout(t);
  }, [value, recId]);
  const Icon = check.status === "checking" ? Loader2 : check.available === true ? Check : check.available === false ? X : AtSign;
  const tone = check.available === true ? "text-emerald-600" : check.available === false ? "text-rose-600" : "text-zinc-400";
  return (
    <Field label="Shop handle" icon={AtSign} testId="field-username">
      <div className="relative">
        <Input value={value} onChange={(e) => onChange(e.target.value.replace(/[^a-zA-Z0-9._-]/g, "").toLowerCase())}
          placeholder="your-handle" className={INPUT_CLS} data-testid="profile-username-input" />
        <Icon size={14} className={`absolute right-3 top-1/2 -translate-y-1/2 ${tone} ${check.status === "checking" ? "animate-spin" : ""}`} />
      </div>
      {value && <div className="text-[11px] mt-1 font-mono text-violet-700">orevital.com/d/{value}</div>}
      {check.message && <div className={`text-[11px] mt-1 ${tone}`}>{check.message}</div>}
    </Field>
  );
}

export function HandleBlock({ form, setForm, slug, recId }) {
  return (
    <>
      <DealerPageCard slug={slug} embedded />
      <UsernameField value={form.user_name || ""} onChange={(v) => setForm((f) => ({ ...f, user_name: v }))} recId={recId} />
    </>
  );
}

const BIO_MAX = 200;

export function BioBlock({ form, setForm, bind, recId }) {
  const len = (form.company || "").length;
  const clamp = (v) => (v || "").slice(0, BIO_MAX);
  return (
    <Field label="Company / brand bio" testId="field-company">
      <div className="flex justify-end mb-1">
        <AiHelperDialog userRecordId={recId} kind="bio" existing={form.company || ""}
          label={form.company ? "Improve with AI" : "Write with AI"} onResult={(v) => setForm((f) => ({ ...f, company: clamp(v) }))} />
      </div>
      <Textarea rows={2} placeholder="Short tagline or description" value={form.company || ""} maxLength={BIO_MAX}
        onChange={(e) => setForm((f) => ({ ...f, company: clamp(e.target.value) }))} className="rounded-xl" data-testid="bio-textarea" />
      <div className={`mt-1 text-right text-xs tabular-nums ${len >= BIO_MAX ? "text-rose-600 font-semibold" : len >= BIO_MAX - 20 ? "text-amber-600" : "text-zinc-400"}`} data-testid="bio-char-count">
        {len} / {BIO_MAX}
      </div>
    </Field>
  );
}

export function AboutUsBlock({ form, setForm, slug, recId }) {
  return (
    <>
      <p className="text-xs text-zinc-500 mb-2">
        This block appears on your public dealer page at <span className="font-mono text-zinc-700">orevital.com/d/{slug || "you"}</span>.
      </p>
      <div className="flex justify-end mb-2">
        <AiHelperDialog userRecordId={recId} kind="about" existing={form.aboutus || ""}
          label={form.aboutus ? "Improve with AI" : "Generate with AI"} onResult={(v) => setForm((f) => ({ ...f, aboutus: v }))} />
      </div>
      <HtmlWidget label="About us" value={form.aboutus} onChange={(v) => setForm((f) => ({ ...f, aboutus: v }))} testId="aboutus-widget" />
    </>
  );
}

export function HomeWidgetBlock({ form }) {
  const html = (form.home_html || "").trim();
  return (
    <>
      <p className="text-xs text-zinc-500 mb-2">
        Your <span className="font-medium text-zinc-700">Home tab</span> is generated from your profile (name, bio, products, posts) and synced from Airtable — it can&apos;t be edited here. Update your profile fields and it refreshes automatically.
      </p>
      {html ? (
        <div className="rounded-xl border border-zinc-200 bg-white overflow-hidden" data-testid="home-html-preview">
          <iframe title="Home tab preview" srcDoc={html} sandbox="" className="w-full h-72 bg-white" />
        </div>
      ) : <p className="text-sm text-zinc-400" data-testid="home-html-empty">No home page content yet.</p>}
    </>
  );
}

export function HomeTabToggle({ checked, onChange, saving }) {
  return (
    <label className="flex items-start gap-3 cursor-pointer rounded-xl border border-zinc-200 bg-white p-4" data-testid="home-tab-toggle-wrap">
      <input type="checkbox" checked={!!checked} disabled={saving} onChange={(e) => onChange(e.target.checked)} className="mt-1 accent-[#0f766e] h-4 w-4" data-testid="home-tab-toggle" />
      <span className="min-w-0">
        <span className="block text-sm font-semibold text-zinc-900">Show a Home tab on my dealer page</span>
        <span className="block text-xs text-zinc-500 mt-0.5">Adds a Home tab (auto-generated page + your chosen post tabs) to your public page. When off, visitors land straight on your products.</span>
      </span>
    </label>
  );
}

export function HomeTabsBlock({ recId, slug }) {
  return <HomePostsPicker userRecordId={recId} slug={slug} />;
}

export function WebSocialBlock({ bind }) {
  return (
    <>
      <Field label="Website" icon={Globe} testId="field-website-url">
        <Input placeholder="https://" {...bind("website_url")} className={INPUT_CLS} />
      </Field>
      <Grid2>
        <Field label="Instagram" icon={Instagram} testId="field-instagram">
          <Input placeholder="@yourhandle" {...bind("dealer_instagram")} className={INPUT_CLS} />
        </Field>
        <Field label="Facebook" icon={Facebook} testId="field-facebook">
          <Input placeholder="facebook.com/…" {...bind("dealer_facebook")} className={INPUT_CLS} />
        </Field>
        <Field label="Twitter / X" icon={Twitter} testId="field-twitter">
          <Input placeholder="@yourhandle" {...bind("dealer_twitter")} className={INPUT_CLS} />
        </Field>
        <Field label="YouTube" icon={Youtube} testId="field-youtube">
          <Input placeholder="youtube.com/@…" {...bind("youtube")} className={INPUT_CLS} />
        </Field>
      </Grid2>
    </>
  );
}

export function PaymentHandlesBlock({ bind }) {
  return (
    <>
      <p className="text-xs text-zinc-500 -mt-1 mb-2">
        These appear on invoices you share with customers so they can pay you directly. Leave blank to use the brand's default handles.
      </p>
      <Grid2>
        <Field label="Venmo" icon={Wallet} testId="field-venmo">
          <Input placeholder="@your-venmo" {...bind("dealer_venmo")} className={INPUT_CLS} />
        </Field>
        <Field label="Zelle (email or phone)" icon={Banknote} testId="field-zelle">
          <Input placeholder="you@email.com or 555-123-4567" {...bind("dealer_zelle")} className={INPUT_CLS} />
        </Field>
      </Grid2>
    </>
  );
}
